import React, { useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  Button,
  IconButton,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Avatar,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material'
import { documentoService } from '@/services/documentoService'
import { RequisicaoFalhou } from '@/services/api'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'
import {
  ReservaOption,
  buscarDocumentosPage,
  carregarReservaOptions,
  enviarDocumentosParaReserva,
} from './documentosFilter'
import { Documento, AnaliseIaResponse } from '@/types/documento'
import { formatoDoMime } from './formatoArquivo'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import {
  CloudUpload,
  Download,
  Delete,
  Visibility as Eye,
  Insights,
  Refresh,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { tokens } from '@/theme'
import { MENSAGEM_POR_CODIGO } from '@/utils/mensagemErroDocumento'

interface DocumentoItem {
  id: string
  nome: string
  tipo: 'PDF' | 'DOCX' | 'XLSX' | 'PNG' | 'JPG' | 'OUTRO'
  tamanhoBytes: number
  status: 'PENDENTE' | 'PROCESSANDO' | 'PROCESSADO' | 'ERRO'
  uploadEm: string
  /** Ausente quando a API não sabe quem subiu — ela guarda só o UUID do usuário. */
  uploadPor?: string
  entidadeNome?: string
  /** A IA identificou tipo diferente do declarado — mostrar o aviso, nunca esconder o doc. */
  tipoDivergente?: boolean
  tipoIdentificadoIa?: string
  reservaId?: string
  /** `tipoEntidade` da API — decide se o CONSULTOR vê a lixeira (só documento de reserva). */
  tipoEntidade?: string
  /** Não vem da API: quem busca por reserva preenche com o rótulo da reserva escolhida. */
  reservaNome?: string
}

const getFileIcon = (tipo: string) => {
  const icons: Record<string, React.ReactNode> = {
    PDF: '📄',
    DOCX: '📝',
    XLSX: '📊',
    PNG: '🖼️',
    JPG: '🖼️',
    OUTRO: '📎',
  }
  return icons[tipo] || '📎'
}

// Cores do theme (R01 #8): nada de hex solto na página.
const getFileColor = (tipo: string): string => {
  const colors: Record<string, string> = {
    PDF: tokens.statusDanger,
    DOCX: tokens.brandPrimary,
    XLSX: tokens.statusSuccess,
    PNG: tokens.statusWarning,
    JPG: tokens.statusWarning,
    OUTRO: tokens.textSecondary,
  }
  return colors[tipo] || tokens.textSecondary
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    PROCESSADO: 'success',
    PROCESSANDO: 'warning',
    PENDENTE: 'info',
    ERRO: 'error',
  }
  return colors[status] || 'default'
}

const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    PROCESSADO: 'Processado',
    PROCESSANDO: 'Processando',
    PENDENTE: 'Pendente',
    ERRO: 'Erro',
  }
  return labels[status] || status
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD/MM/YYYY HH:mm')
}

const DropZone: React.FC<{
  reserva: ReservaOption | null
  onUploaded: () => void
  isLoading: boolean
}> = ({ reserva, onUploaded, isLoading }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()

  // Upload só é permitido com uma reserva selecionada (vínculo obrigatório).
  const disabled = isLoading || isUploading || !reserva

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleFiles = async (fileList: FileList) => {
    if (!reserva) {
      enqueueSnackbar('Selecione uma reserva antes de enviar documentos.', {
        variant: 'warning',
      })
      return
    }
    const files = Array.from(fileList)
    if (files.length === 0) return

    setIsUploading(true)
    try {
      await enviarDocumentosParaReserva(files, reserva.id)
      enqueueSnackbar(
        `${files.length} arquivo(s) enviado(s) e vinculado(s) à reserva.`,
        { variant: 'success' }
      )
      onUploaded()
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Falha ao enviar arquivo(s)',
        { variant: 'error' }
      )
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (disabled) return
    handleFiles(e.dataTransfer.files)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files)
    }
  }

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        border: '2px dashed',
        borderColor: isDragOver ? 'primary.main' : 'divider',
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: !reserva && !isUploading ? 0.6 : 1,
        backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: disabled ? 'divider' : 'primary.main',
          backgroundColor: disabled ? 'background.paper' : 'action.hover',
        },
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
        onChange={handleFileInputChange}
        disabled={disabled}
      />

      {isUploading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">
            Enviando e vinculando à reserva...
          </Typography>
          <LinearProgress />
        </Box>
      ) : (
        <>
          <CloudUpload
            sx={{
              fontSize: 48,
              color: 'primary.main',
              mb: 1,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
            }}
          >
            Arraste arquivos aqui ou clique para selecionar
          </Typography>
          {!reserva ? (
            <Alert severity="info" sx={{ mb: 2, justifyContent: 'center' }}>
              Selecione a <strong>reserva de destino</strong> acima para habilitar o
              envio.
            </Alert>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Tamanho máximo: 50 MB
            </Typography>
          )}
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
            {['PDF', 'DOCX', 'XLSX', 'PNG', 'JPG'].map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
          <Button
            variant="contained"
            onClick={() => fileInputRef.current?.click()}
            sx={{ mt: 2 }}
            disabled={disabled}
          >
            Selecionar Arquivos
          </Button>
        </>
      )}
    </Box>
  )
}

const DocumentoCard: React.FC<{
  documento: DocumentoItem
  onDelete: (id: string) => void
  onReprocessado?: () => void
  /** Falso esconde a lixeira (consultor só remove documento de reserva — VC-16). */
  podeExcluir?: boolean
}> = ({ documento, onDelete, onReprocessado, podeExcluir = true }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [baixando, setBaixando] = useState(false)
  const [reprocessando, setReprocessando] = useState(false)
  const [analiseOpen, setAnaliseOpen] = useState(false)
  const [analise, setAnalise] = useState<AnaliseIaResponse | null>(null)
  const [analiseLoading, setAnaliseLoading] = useState(false)
  const [analiseErro, setAnaliseErro] = useState<string | null>(null)

  const handleAnalise = async () => {
    setAnaliseOpen(true)
    setAnaliseLoading(true)
    setAnaliseErro(null)
    setAnalise(null)
    try {
      setAnalise(await documentoService.obterAnalise(documento.id))
    } catch (e) {
      const status = (e as { status?: number })?.status
      setAnaliseErro(
        status === 404
          ? 'Análise de IA ainda não disponível para este documento.'
          : (e as { message?: string })?.message ?? 'Falha ao carregar a análise.',
      )
    } finally {
      setAnaliseLoading(false)
    }
  }

  // Abre (visualizar) ou baixa o arquivo via stream do backend.
  const abrirArquivo = async (download: boolean) => {
    setBaixando(true)
    try {
      const blob = await documentoService.baixarArquivo(documento.id)
      const url = URL.createObjectURL(blob)
      if (download) {
        const a = document.createElement('a')
        a.href = url
        a.download = documento.nome
        document.body.appendChild(a)
        a.click()
        a.remove()
      } else {
        window.open(url, '_blank', 'noopener')
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message ?? 'Falha ao abrir o documento', {
        variant: 'error',
      })
    } finally {
      setBaixando(false)
    }
  }

  const handleReprocessar = async () => {
    setReprocessando(true)
    try {
      await documentoService.reprocessar(documento.id)
      enqueueSnackbar('Documento reprocessado', { variant: 'success' })
      onReprocessado?.()
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message ?? 'Falha ao reprocessar', {
        variant: 'error',
      })
    } finally {
      setReprocessando(false)
    }
  }

  const handleDelete = () => {
    // O sucesso (ou a recusa, com o motivo do backend) é anunciado por quem chama a API —
    // anunciar aqui dizia "excluído com sucesso" também quando o backend recusava.
    onDelete(documento.id)
  }

  const colorFile = getFileColor(documento.tipo)
  const statusColor = getStatusColor(documento.status)

  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <Avatar
        sx={{
          width: 60,
          height: 60,
          bgcolor: colorFile,
          fontSize: 24,
          flexShrink: 0,
        }}
      >
        {getFileIcon(documento.tipo)}
      </Avatar>

      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Tooltip title={documento.nome}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '300px',
              }}
            >
              {documento.nome}
            </Typography>
          </Tooltip>
          {documento.status === 'PROCESSANDO' && (
            <CircularProgress
              size={20}
              sx={{
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${documento.tipo} • ${formatFileSize(documento.tamanhoBytes)}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={getStatusLabel(documento.status)}
            size="small"
            color={statusColor}
            variant="outlined"
          />
          {documento.tipoDivergente && (
            <Tooltip
              title={`A IA identificou este arquivo como ${documento.tipoIdentificadoIa ?? 'outro tipo'} — confira se o anexo corresponde ao documento pedido.`}
            >
              <Chip label="Tipo divergente" size="small" color="warning" />
            </Tooltip>
          )}
        </Stack>

        {documento.reservaNome ? (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
            Reserva: <strong style={{ color: tokens.brandPrimary }}>{documento.reservaNome}</strong>
          </Typography>
        ) : documento.entidadeNome ? (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
            Entidade: <strong>{documento.entidadeNome}</strong>
          </Typography>
        ) : null}

        <Typography variant="caption" color="textSecondary">
          {documento.uploadPor
            ? `Enviado por ${documento.uploadPor} em ${formatDate(documento.uploadEm)}`
            : `Enviado em ${formatDate(documento.uploadEm)}`}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Tooltip title="Ver documento">
          <IconButton
            size="small"
            color="primary"
            onClick={() => abrirArquivo(false)}
            disabled={baixando}
          >
            <Eye fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Baixar">
          <IconButton size="small" onClick={() => abrirArquivo(true)} disabled={baixando}>
            <Download fontSize="small" />
          </IconButton>
        </Tooltip>
        {documento.status === 'ERRO' && (
          <Tooltip title="Reprocessar">
            <IconButton
              size="small"
              color="warning"
              onClick={handleReprocessar}
              disabled={reprocessando}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Análise IA">
          <IconButton size="small" onClick={handleAnalise} disabled={analiseLoading}>
            <Insights fontSize="small" />
          </IconButton>
        </Tooltip>
        {podeExcluir && (
          <Tooltip title="Excluir">
            <IconButton size="small" color="error" onClick={handleDelete}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Dialog open={analiseOpen} onClose={() => setAnaliseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Análise de IA — {documento.nome}</DialogTitle>
        <DialogContent dividers>
          {analiseLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : analiseErro ? (
            <Alert severity="info">{analiseErro}</Alert>
          ) : analise ? (
            <Stack spacing={2}>
              {analise.tipoIdentificado && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Tipo identificado
                  </Typography>
                  <Typography variant="body2">
                    {analise.tipoIdentificado}
                    {typeof analise.confidenciaIdentificacao === 'number' &&
                      ` (${Math.round(analise.confidenciaIdentificacao * 100)}%)`}
                  </Typography>
                </Box>
              )}
              {analise.riscoIdentificado && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Risco
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip size="small" label={analise.riscoIdentificado} />
                  </Box>
                </Box>
              )}
              {analise.resumo && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Resumo
                  </Typography>
                  <Typography variant="body2">{analise.resumo}</Typography>
                </Box>
              )}
              {analise.pontosChave && analise.pontosChave.length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Pontos-chave
                  </Typography>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {analise.pontosChave.map((p, i) => (
                      <li key={i}>
                        <Typography variant="body2">{p}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
              {analise.recomendacoes && analise.recomendacoes.length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Recomendações
                  </Typography>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {analise.recomendacoes.map((r, i) => (
                      <li key={i}>
                        <Typography variant="body2">{r}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
              {analise.dadosExtraidos && Object.keys(analise.dadosExtraidos).length > 0 && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Dados extraídos
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {Object.entries(analise.dadosExtraidos).map(([k, v]) => (
                      <Typography key={k} variant="body2">
                        <strong>{k}:</strong> {String(v)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
              {(analise.modeloUtilizado || analise.dataAnalise) && (
                <>
                  <Divider />
                  <Typography variant="caption" color="textSecondary">
                    {analise.modeloUtilizado ? `Modelo: ${analise.modeloUtilizado}` : ''}
                    {analise.modeloUtilizado && analise.dataAnalise ? ' · ' : ''}
                    {analise.dataAnalise ? `Analisado em ${formatDate(analise.dataAnalise)}` : ''}
                  </Typography>
                </>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnaliseOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}


// O `Record<string, any>` que existia aqui deixava ler campos que a API nunca envia:
// `uploadPor` e `reservaNome` caíam sempre no fallback, e a listagem dizia "Enviado por
// Sistema" para todo documento. Tipado só com `Documento`, o `tsc` cobra a diferença.
function toItem(doc: Documento): DocumentoItem {
  return {
    id: doc.id,
    nome: doc.nome,
    tipo: formatoDoMime(doc.mimeType),
    tamanhoBytes: doc.tamanhoBytes ?? 0,
    status: doc.status as DocumentoItem['status'],
    uploadEm: doc.dataUpload ?? doc.createdAt,
    // O backend guarda o UUID de quem subiu, não o nome; resolver isso exige um join com
    // `auth` que ainda não existe. Melhor omitir do que inventar um autor.
    uploadPor: undefined,
    entidadeNome: doc.entidadeReferencia,
    tipoDivergente: doc.tipoDivergente ?? false,
    tipoIdentificadoIa: doc.tipoIdentificadoIa,
    reservaId: doc.tipoEntidade === TIPO_ENTIDADE_OPORTUNIDADE ? doc.entidadeReferencia : undefined,
    tipoEntidade: doc.tipoEntidade,
  }
}

/**
 * Gestão de documentos. `embutida` (Onda 2 / A04): renderizada como aba de
 * "Pendências e Documentos", que já traz PageHeader e KPIs — o cabeçalho local sai.
 */
const DocumentosListPage: React.FC<{ embutida?: boolean }> = ({ embutida = false }) => {
  const [documentos, setDocumentos] = useState<DocumentoItem[]>([])
  const [filterTipo, setFilterTipo] = useState<string>('TODOS')
  const [filterStatus, setFilterStatus] = useState<string>('TODOS')
  const [filterReserva, setFilterReserva] = useState<ReservaOption | null>(null)
  const [uploadReserva, setUploadReserva] = useState<ReservaOption | null>(null)
  const [reservas, setReservas] = useState<ReservaOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  // A tela não lista nada ao abrir — o usuário precisa aplicar os filtros e
  // clicar em "Buscar". `hasSearched` controla o estado inicial vs. resultado.
  const [hasSearched, setHasSearched] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const { enqueueSnackbar } = useSnackbar()
  // O backend decide (remoção lógica, janela RASCUNHO/COM_PENDENCIA, 409 com motivo); a tela
  // só não oferece a lixeira ao consultor onde a resposta seria sempre "não removível".
  const consultor = ehConsultor(authStore((st) => st.usuario)?.permissoes)

  // Busca os documentos de uma reserva específica no BACKEND (já escopado por
  // tenant: consultor só vê o que é do seu tenant). A seleção de reserva é
  // obrigatória — sem ela não há busca.
  const buscarPorReserva = useCallback(async (reserva: ReservaOption) => {
    setIsLoading(true)
    setHasSearched(true)
    setErro(null)
    try {
      const page = await buscarDocumentosPage(reserva.id)
      const lista = (page.content ?? []).map((d) => {
        const item = toItem(d)
        // Backend pode não popular reservaNome; usa o rótulo da reserva selecionada.
        if (!item.reservaNome) item.reservaNome = reserva.label
        return item
      })
      setDocumentos(lista)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar documentos')
      setDocumentos([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Busca usando a reserva atualmente selecionada no filtro (botão "Buscar").
  const carregar = useCallback(async () => {
    if (!filterReserva) return
    await buscarPorReserva(filterReserva)
  }, [filterReserva, buscarPorReserva])

  // Após um upload, alinha o filtro à reserva de destino e mostra seus documentos.
  const handleUploaded = useCallback(() => {
    if (!uploadReserva) return
    setFilterReserva(uploadReserva)
    void buscarPorReserva(uploadReserva)
  }, [uploadReserva, buscarPorReserva])

  // Limpa filtros e volta ao estado inicial (sem lista).
  const limparFiltros = useCallback(() => {
    setFilterReserva(null)
    setFilterTipo('TODOS')
    setFilterStatus('TODOS')
    setDocumentos([])
    setHasSearched(false)
    setErro(null)
  }, [])

  // Lista de reservas (oportunidades) para o filtro, com rótulo "Empresa — Produto".
  const carregarReservas = useCallback(async () => {
    try {
      setReservas(await carregarReservaOptions())
    } catch {
      // Falha silenciosa: o filtro fica vazio, mas a busca segue funcionando.
    }
  }, [])

  useEffect(() => {
    carregarReservas()
  }, [carregarReservas])

  const handleDeleteDocumento = async (id: string) => {
    try {
      await documentoService.deletar(id)
      setDocumentos(documentos.filter((d) => d.id !== id))
      enqueueSnackbar('Documento excluído', { variant: 'success' })
    } catch (e) {
      // A recusa do backend explica o motivo (ex.: "só com a reserva em rascunho ou com
      // pendência") — mostrar a mensagem dele, não um genérico.
      // Onda 5: o informativo vigente de produto não pode ser removido (409).
      const codigo = (e as RequisicaoFalhou | undefined)?.data as { errorCode?: string } | undefined
      const mensagem =
        codigo?.errorCode === 'DOCUMENTO_E_INFORMATIVO_VIGENTE'
          ? MENSAGEM_POR_CODIGO.DOCUMENTO_E_INFORMATIVO_VIGENTE
          : (e as RequisicaoFalhou | undefined)?.message
      enqueueSnackbar(mensagem || 'Falha ao excluir documento', { variant: 'error' })
    }
  }

  const filteredDocumentos = documentos.filter((doc) => {
    const tipoMatch = filterTipo === 'TODOS' || doc.tipo === filterTipo
    const statusMatch = filterStatus === 'TODOS' || doc.status === filterStatus
    const typeMatch = filterTipo === 'IMAGEM' ? ['PNG', 'JPG'].includes(doc.tipo) : true
    return tipoMatch && statusMatch && typeMatch
  })

  const statusStats = {
    PROCESSADO: documentos.filter((d) => d.status === 'PROCESSADO').length,
    PROCESSANDO: documentos.filter((d) => d.status === 'PROCESSANDO').length,
    PENDENTE: documentos.filter((d) => d.status === 'PENDENTE').length,
    ERRO: documentos.filter((d) => d.status === 'ERRO').length,
  }

  return (
    <Box sx={{ p: embutida ? 0 : 3 }}>
      {!embutida && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Gestão de Documentos
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Centralize, organize e analise todos os seus documentos com IA
          </Typography>
        </Box>
      )}

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar os documentos. {erro}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Enviar documento
        </Typography>
        <Autocomplete
          size="small"
          sx={{ mb: 2, maxWidth: 480 }}
          options={reservas}
          value={uploadReserva}
          onChange={(_, value) => setUploadReserva(value)}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          getOptionLabel={(opt) => opt.label}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Reserva de destino"
              placeholder="Selecione a reserva"
              error={!uploadReserva}
              helperText={
                uploadReserva
                  ? 'Os arquivos enviados serão vinculados a esta reserva.'
                  : 'Obrigatório: selecione a reserva para vincular o documento.'
              }
            />
          )}
          noOptionsText="Nenhuma reserva encontrada"
        />
        <DropZone reserva={uploadReserva} onUploaded={handleUploaded} isLoading={isLoading} />
      </Card>

      <Card sx={{ p: 3, mb: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={reservas}
              value={filterReserva}
              onChange={(_, value) => setFilterReserva(value)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => opt.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reserva"
                  placeholder="Todas as reservas"
                />
              )}
              noOptionsText="Nenhuma reserva encontrada"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Arquivo</InputLabel>
              <Select
                value={filterTipo}
                label="Tipo de Arquivo"
                onChange={(e) => setFilterTipo(e.target.value)}
              >
                <MenuItem value="TODOS">Todos os tipos</MenuItem>
                <MenuItem value="PDF">PDF</MenuItem>
                <MenuItem value="DOCX">DOCX</MenuItem>
                <MenuItem value="XLSX">XLSX</MenuItem>
                <MenuItem value="IMAGEM">Imagens (PNG/JPG)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ alignSelf: 'center', fontWeight: 600 }}>
                Status:
              </Typography>
              {[
                { label: 'Todos', value: 'TODOS', count: documentos.length },
                { label: 'Processado', value: 'PROCESSADO', count: statusStats.PROCESSADO },
                { label: 'Processando', value: 'PROCESSANDO', count: statusStats.PROCESSANDO },
                { label: 'Pendente', value: 'PENDENTE', count: statusStats.PENDENTE },
                { label: 'Erro', value: 'ERRO', count: statusStats.ERRO },
              ].map((item) => (
                <Chip
                  key={item.value}
                  label={`${item.label} (${item.count})`}
                  onClick={() => setFilterStatus(item.value)}
                  variant={filterStatus === item.value ? 'filled' : 'outlined'}
                  color={filterStatus === item.value ? 'primary' : 'default'}
                />
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={carregar}
                disabled={isLoading || !filterReserva}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {isLoading ? 'Buscando…' : 'Buscar'}
              </Button>
              <Button variant="outlined" onClick={limparFiltros} disabled={isLoading}>
                Limpar
              </Button>
              {!filterReserva && (
                <Typography variant="caption" color="textSecondary">
                  Selecione uma reserva para habilitar a busca.
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {!hasSearched ? (
        <Alert severity="info">
          Selecione uma <strong>reserva</strong> e clique em <strong>Buscar</strong> para listar os
          documentos.
        </Alert>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredDocumentos.length === 0 ? (
        <Alert severity="info">Nenhum documento encontrado com os filtros selecionados.</Alert>
      ) : (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: 'textSecondary' }}>
            Total: <strong>{filteredDocumentos.length}</strong> documento(s)
          </Typography>
          {filteredDocumentos.map((documento) => (
            <DocumentoCard
              key={documento.id}
              documento={documento}
              onDelete={handleDeleteDocumento}
              onReprocessado={carregar}
              podeExcluir={!consultor || documento.tipoEntidade === TIPO_ENTIDADE_OPORTUNIDADE}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default DocumentosListPage
